import { useState, useRef, useCallback, useEffect } from "react";
import SparkMD5 from "spark-md5";
import { api, deferSignOut } from "../api";
import MessagePreview from "../components/MessagePreview";
import { G, G_DARK, G_BG, Badge, Toggle, Field, Input, Select, Btn, Spinner, ProgressBar, SrOnly } from "../components/Shared";
import { useDocumentWord } from "../documentWord";

const STEPS = ["Files", "Review"];

// Goes up to PB: a single submission here is routinely hundreds of GB and may
// be several TB, and stopping at GB rendered those as "4657.66 GB".
export function fmtBytes(b) {
  if (b < 1024)      return b + " B";
  if (b < 1024 ** 2)  return (b / 1024).toFixed(1) + " KB";
  if (b < 1024 ** 3)  return (b / 1024 ** 2).toFixed(1) + " MB";
  if (b < 1024 ** 4)  return (b / 1024 ** 3).toFixed(2) + " GB";
  if (b < 1024 ** 5)  return (b / 1024 ** 4).toFixed(2) + " TB";
  return (b / 1024 ** 5).toFixed(2) + " PB";
}

// S3 caps a single object at 5 TiB — mirrors S3_MAX_OBJECT_BYTES in the
// backend, which stays the authority. Checked here only so the file picker can
// say so immediately instead of at submit/start.
export const MAX_FILE_BYTES = 5 * 1024 ** 4;

// An upload runs inside the sign-in session and is abandoned if the session
// ends first. JOB-529B91 was 948 GB needing 21.1 h against an 8 h session —
// impossible from the first byte, and discovered eight hours later.
//
// Same argument as the MAX_FILE_BYTES check below: the failure worth avoiding
// is the one that only shows up after hours of transfer. The difference is
// that 5 TiB is a hard S3 fact and this is a projection, so this one *warns*
// and never blocks — the wizard cannot know how fast the link is, and refusing
// an upload that would have succeeded is worse than letting it run.
//
// 45 GB/h is 100 Mbit/s, the rate measured on JOB-529B91. Quoted as a rate
// rather than a total because a total is only true at one session length.
export const ASSUMED_BYTES_PER_HOUR = 45 * 1024 ** 3;

// Don't project from the first few seconds: the opening moments are dominated
// by connection setup and are not representative of the sustained rate.
//
// Gated on elapsed time and on an absolute number of bytes — deliberately NOT
// on a *fraction* of the submission, which was the first attempt and is worse
// the bigger the file. At the rate JOB-529B91 actually ran, 1% of 948 GB takes
// about 21 minutes, so a fraction gate would have withheld the warning for
// exactly the uploads that most need it. A slow rate measured over a minute is
// not noise; it is the finding.
export const MIN_PROJECTION_MS = 60_000;
export const MIN_PROJECTION_BYTES = 8 * 1024 ** 2;

export function fmtHours(h) {
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  return h < 10 ? `${h.toFixed(1)} h` : `${Math.round(h)} h`;
}

/** Hours left in the sign-in session, or null when it can't be known. */
export function hoursLeft(expiresAt, now = Date.now()) {
  if (!expiresAt) return null;
  return Math.max(0, (expiresAt.getTime() - now) / 3_600_000);
}

/**
 * Before anything moves: does this selection plausibly fit in what's left of
 * the session? Returns null when there is nothing useful to say.
 */
export function preflightEstimate(totalBytes, expiresAt, now = Date.now()) {
  const left = hoursLeft(expiresAt, now);
  if (!totalBytes || left === null) return null;
  const needed = totalBytes / ASSUMED_BYTES_PER_HOUR;
  return { needed, left, fits: needed <= left };
}

/**
 * While running: project from what the transfer is *actually* doing, which is
 * the only estimate worth acting on. This is the check that would have caught
 * JOB-529B91 after a few minutes instead of after eight hours.
 */
export function liveProjection(loaded, total, startedAt, expiresAt, now = Date.now()) {
  if (!startedAt || !total || !loaded) return null;
  const elapsedMs = now - startedAt;
  if (elapsedMs < MIN_PROJECTION_MS || loaded < MIN_PROJECTION_BYTES) return null;

  const bytesPerHour = loaded / (elapsedMs / 3_600_000);
  if (!bytesPerHour) return null;
  const remainingHours = (total - loaded) / bytesPerHour;
  const left = hoursLeft(expiresAt, now);
  return {
    remainingHours,
    bytesPerHour,
    left,
    // Only ever false when there is a session end to compare against.
    fits: left === null ? true : remainingHours <= left,
  };
}

// Mirrors the backend's submit/start date-time format (%Y-%m-%d-%H-%M) —
// display-only, just to preview the shape of the auto-assigned path. **In UTC**,
// because the server stamps it with `datetime.now(timezone.utc)`; until
// 2026-10-30 this read the browser's local clock and previewed a folder an hour
// or two off the one the files land in.
export function exampleDatePath(d = new Date()) {
  const p = n => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}-${p(d.getUTCHours())}-${p(d.getUTCMinutes())}`;
}

// ── Which quotation is this data for? ───────────────────────────────────────
//
// **`order` here is an identifier, never a word a person reads.** A quotation
// IS the piece of agreed work — one quotation, one deadline, one record sent at
// the end — and the facility's decision of 2026-09-16 is that every screen,
// message and manual says exactly that. `order_id`, `orderChoice`,
// `ORDER_LIVE_STATUSES` and the `/orders` route keep the backend's names on
// purpose, because renaming a route is a contract change for one word. Said
// once for this file rather than on every line below.

/**
 * The statuses that still take data — mirrors ORDER_LIVE_STATUSES in
 * backend/portal/models/quotation.py, which stays the authority. Only these are
 * offered, because only these are what `_order_for_transfer` will accept.
 *
 * Tied to that file by a test that READS it, not by this sentence. A constant
 * copied across a repository boundary drifts silently, and a test asserting
 * this list against a fixture built from the same two words would only mirror
 * the copy — the guard-matching-its-own-docstring shape. If the backend gains a
 * live status and this does not, `liveOrders` drops those quotations,
 * `mustChoose` goes false on a project that has several, and nothing sends an
 * `order_id` while the server refuses for want of one.
 */
export const ORDER_LIVE_STATUSES = ["draft", "open"];

export function liveOrders(rows) {
  return (rows || []).filter(o => o && ORDER_LIVE_STATUSES.includes(o.status));
}

/**
 * The clock, in words. `days_left` is negative once the deadline has passed and
 * `overdue` says so outright — read `overdue` first, because it is derived from
 * `deadline <= now`, the same comparison the upload gate refuses on, while the
 * floored day count reads 0 at exactly the deadline.
 *
 * Both come off the API. Neither is recomputed here: the newsletter renders the
 * same two values into somebody's inbox, and a second rounding would have the
 * mail and this picker disagreeing by a day about the same quotation.
 */
export function orderCountdown(o) {
  if (o.overdue || o.days_left < 0) return "overdue";
  if (o.days_left === null || o.days_left === undefined) return "no deadline";
  if (o.days_left === 0) return "due today";
  return `${o.days_left} day${o.days_left === 1 ? "" : "s"} left`;
}

export function orderLabel(o) {
  const parts = [o.number || `#${o.id}`];
  if (o.title) parts.push(o.title);
  parts.push(orderCountdown(o));
  return parts.join(" — ");
}

/**
 * Which quotation a transfer belongs to — the facility's decision of 2026-09-16.
 *
 * One live quotation is chosen by the server and no picker appears: a click
 * added for a choice that does not exist is a click for nothing, and that is the
 * ordinary case. Several, and the person picks. None, and there is nothing to
 * pick from — the server's own refusal about an unquoted project is the useful
 * message, and putting an empty picker in front of it would bury it.
 *
 * **Nothing is pre-selected.** The quotation is the piece of agreed work this
 * data becomes part of, and defaulting to the newest is how three months of
 * work gets filed against the wrong agreement.
 *
 * **With one live quotation the id is deliberately NOT sent.** `order_id` is
 * optional on every transfer route for exactly this case, and leaving it out
 * lets the server decide at the moment the upload starts rather than acting on
 * what this page read minutes ago — a quotation that closed in between would
 * otherwise come back as "not one of this project's" instead of being replaced
 * by whatever is live now.
 *
 * A list that cannot be fetched is not a block. The picker is a convenience;
 * the gate is the server, and `_order_for_transfer` answers 409 naming every
 * open quotation when it needs one. Refusing to upload because a helper request
 * failed would stand between somebody and a transfer for no gain.
 */
export function useTransferOrder(projectId) {
  // Which piece of work, among several: an aggregate question, so the word
  // for the current setting.
  const word = useDocumentWord();
  const [live, setLive] = useState([]);
  const [chosen, setChosen] = useState("");   // "" — never undefined, or the
                                              // <select> goes uncontrolled

  const reload = useCallback(() => api.get(`/api/projects/${projectId}/orders`)
    .then(rows => setLive(liveOrders(rows)))
    .catch(() => {}), [projectId]);

  useEffect(() => { reload(); }, [reload]);

  // A quotation that has closed since the page loaded is no longer a valid
  // answer, so the choice is CLEARED — not merely hidden.
  //
  // It used to be `live.some(...) ? chosen : ""` and nothing more, which
  // un-displayed the id and left it in state. A later reload returning that
  // quotation again — an admin reopens it, or one list omits it and the next
  // does not — silently re-selected something the person last saw unselected,
  // and `query` started sending it. "Not shown" and "not chosen" have to be the
  // same state (2026-09-16).
  const stillLive = !chosen || live.some(o => String(o.id) === chosen);
  useEffect(() => {
    if (!stillLive) setChosen("");
  }, [stillLive]);
  // Derived for THIS render as well as cleared by the effect above, which lands
  // after it: one render sending a closed quotation's id is one 404 too many.
  const chosenId = stillLive ? chosen : "";
  const mustChoose = live.length > 1;

  return {
    live,
    chosen: chosenId,
    word,
    setChosen,
    reload,
    mustChoose,
    // The one thing standing between this and the upload, or null.
    blockedBy: mustChoose && !chosenId
      ? `Choose the ${word()} this data belongs to.`
      : null,
    // Appended to the transfer's start call. `order_id` is a QUERY parameter on
    // submit/start, results/upload/start and results — see the routes.
    query: chosenId ? `?order_id=${encodeURIComponent(chosenId)}` : "",
  };
}

/**
 * The picker itself. Rendered only when there is a choice to make, so it has no
 * "not applicable" state to explain.
 */
export function OrderPicker({ orders, value, onChange, disabled }) {
  const word = useDocumentWord();
  return (
    <Field
      label={`${word({ capital: true })} this data belongs to`}
      required
      hint={`This project has more than one open ${word()}. Nothing is chosen for you — the ${word()} is the piece of agreed work, and the record sent when it ends names it.`}
    >
      <Select
        options={orders.map(o => ({ value: String(o.id), label: orderLabel(o) }))}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      />
    </Field>
  );
}

const makeInitialForm = project => ({
  notifyEmail: project.group_operator_email || "", notifyOnComplete: true,
});

/**
 * Sorts dropped or picked entries into what can be uploaded and what cannot.
 *
 * **A dropped folder arrives in `dataTransfer.files` as an ordinary File of
 * size 0**, with no type and no extension — indistinguishable from a file by
 * anything except its size. Uploaded, it produced a 0-byte object whose MD5 is
 * the MD5 of nothing, which then *verified successfully* because empty matched
 * empty. A researcher submitted four folders that way and the portal reported
 * every one of them Completed while storing none of their data.
 *
 * So nothing empty is ever accepted. A genuinely empty file carries no data
 * either, and refusing it costs nothing next to silently discarding a
 * submission.
 */
export const EMPTY_MD5 = "d41d8cd98f00b204e9800998ecf8427e";

export function sortSelection(entries) {
  const accepted = [], folders = [], empty = [];
  for (const { file, isDirectory } of entries) {
    // webkitGetAsEntry() is authoritative when the browser gives it to us; the
    // size test catches the rest, including the file picker.
    if (isDirectory) folders.push(file.name);
    else if (!file.size) empty.push(file.name);
    else accepted.push(file);
  }
  return { accepted, folders, empty };
}

export function selectionProblem({ folders, empty }) {
  const parts = [];
  if (folders.length) {
    parts.push(
      `${folders.length === 1 ? "Folder" : "Folders"} cannot be uploaded directly ` +
      `(${folders.join(", ")}). Open the folder and select the files inside, or ` +
      `zip it first and upload the zip.`);
  }
  if (empty.length) {
    parts.push(
      `${empty.length === 1 ? "This file is" : "These files are"} empty and ` +
      `${empty.length === 1 ? "was" : "were"} not added (${empty.join(", ")}). ` +
      `An empty file carries no data — check it is what you meant to send.`);
  }
  return parts.join(" ") || null;
}

// Exported so the delivery upload can use the same dropzone. It is the layer
// that refuses a dropped FOLDER — e.dataTransfer hands one over as an ordinary
// 0-byte File, and four of them were once stored, reported Completed and
// verified. A second dropzone would have to learn that again.
export function FileDropZone({ files, onFiles }) {
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState(null);
  const inputRef = useRef();

  const add = entries => {
    const sorted = sortSelection(entries);
    setProblem(selectionProblem(sorted));
    if (sorted.accepted.length) {
      onFiles(prev => [...prev,
        ...sorted.accepted.map(f => ({ file: f, id: Math.random().toString(36).slice(2) }))]);
    }
  };

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    // `items` carries webkitGetAsEntry(), which is the only reliable way to
    // tell a folder from a file before reading it. `files` alone cannot.
    const items = e.dataTransfer.items ? Array.from(e.dataTransfer.items) : [];
    const entries = Array.from(e.dataTransfer.files).map((file, i) => {
      const item = items[i];
      const entry = item && item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
      return { file, isDirectory: entry ? entry.isDirectory : false };
    });
    add(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFiles]);

  const handleInput = e => {
    const picked = Array.from(e.target.files).map(file => ({ file, isDirectory: false }));
    e.target.value = "";
    add(picked);
  };

  return (
    <div>
      {/* Drag-and-drop is a pointer gesture with no keyboard equivalent, so
          this doubles as a real button: focusable, activated by Enter/Space,
          and named — otherwise the whole submission flow is unreachable
          without a mouse (WCAG 2.1.1). */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Choose files to upload, or drop them here"
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current.click()}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current.click();
          }
        }}
        style={{
          border: `2px dashed ${dragging ? G : "#9caa9c"}`,
          borderRadius: 12, padding: "40px 24px", textAlign: "center",
          cursor: "pointer", background: dragging ? G_BG : "#fafafa",
          transition: "all 0.2s",
        }}
      >
        <div aria-hidden="true" style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
        <div style={{ color: "#1a1a1a", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Drop files here</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>Any data files accepted · Click or press Enter to browse</div>
        <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={handleInput} />
      </div>

      {/* role="alert": unlike the standing session notice, this is a direct
          response to something the user just did, and they must not miss it —
          silently ignoring a dropped folder is how the data went missing. */}
      {problem && (
        <div role="alert" style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 8,
          background: "rgba(217,119,6,0.07)", border: "1px solid rgba(217,119,6,0.4)",
          color: "#92400e", fontSize: 13,
        }}>
          {problem}
        </div>
      )}

      {files.length > 0 && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {files.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
              <Badge color="blue">{f.file.name.split(".").pop().toUpperCase()}</Badge>
              <span style={{ color: "#1a1a1a", fontSize: 13, flex: 1, fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.file.name}
              </span>
              <span style={{ color: "#6b7280", fontSize: 12, flexShrink: 0 }}>{fmtBytes(f.file.size)}</span>
              <button
                type="button"
                // Named, and big enough to hit — a bare "×" glyph is both
                // unlabelled and, at 18px, under the 24×24 minimum target.
                aria-label={`Remove ${f.file.name}`}
                onClick={e => { e.stopPropagation(); onFiles(prev => prev.filter(x => x.id !== f.id)); }}
                style={{
                  background: "none", border: "none", color: "#6b7280", cursor: "pointer",
                  fontSize: 18, lineHeight: 1, minWidth: 24, minHeight: 24,
                  display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
                }}
              >×</button>
            </div>
          ))}
          <div style={{ padding: "8px 14px", borderRadius: 8, background: G_BG, border: `1px solid rgba(0,150,64,0.2)`, display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ color: G, fontSize: 15 }}>✓</span>
            <span style={{ color: G_DARK, fontSize: 13, fontWeight: 600 }}>{files.length} file{files.length > 1 ? "s" : ""} staged</span>
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              — total: {fmtBytes(files.reduce((a, f) => a + f.file.size, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StepFiles({ files, setFiles }) {
  return <FileDropZone files={files} onFiles={setFiles} />;
}

function StepReview({ project, form, setForm, files, orderChoice, resuming }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const Row = ({ label, value, mono }) => (
    <div>
      <span style={{ color: "#6b7280", fontSize: 12 }}>{label}: </span>
      <span style={{ color: "#1a1a1a", fontSize: 13, fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}>
        {value || <span style={{ color: "#6b7280" }}>—</span>}
      </span>
    </div>
  );
  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#374151", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}>{title}</div>
      <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>{children}</div>
    </div>
  );
  return (
    <div>
      {/* Only when there IS a choice. One open quotation is picked by the
          server and needs no click here; none at all is a project nobody has
          quoted, and an empty picker would sit on top of the message that says
          so.

          And not on a resume: continuing an unfinished upload never calls
          submit/start, so the answer would be collected and thrown away. See
          `choiceNeeded` below. */}
      {orderChoice.mustChoose && !resuming && (
        <div style={{ marginBottom: 18 }}>
          <OrderPicker
            orders={orderChoice.live}
            value={orderChoice.chosen}
            onChange={orderChoice.setChosen}
          />
        </div>
      )}
      {orderChoice.mustChoose && resuming && (
        <div role="status" style={{ marginBottom: 18, fontSize: 12, color: "#6b7280" }}>
          This upload continues the one that stopped, so it stays with the {orderChoice.word()}
          {" "}it was started against — there is nothing to choose here.
        </div>
      )}
      <Section title="Project">
        <Row label="Name" value={project.name} />
        <Row label="Representative email" value={project.representative_email} />
      </Section>
      <Section title="Files">
        <div style={{ gridColumn: "1/-1", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {files.map(f => <Badge key={f.id} color="green">{f.file.name}</Badge>)}
        </div>
      </Section>
      <Section title="S3 Upload Destination">
        <Row label="Bucket" value={project.s3_bucket} mono />
        <Row label="Region" value={project.s3_region} mono />
        <Row label="Prefix" value={project.s3_prefix_base} mono />
        {project.s3_endpoint_url && <Row label="Endpoint URL" value={project.s3_endpoint_url} mono />}
        <div style={{ gridColumn: "1/-1", color: "#6b7280", fontSize: 11 }}>
          A folder dated in UTC and a unique job ID (e.g. {exampleDatePath()}/JOB-4F2A9C) are appended automatically on submit.
        </div>
      </Section>
      <div style={{ padding: "16px 18px", borderRadius: 10, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#374151", marginBottom: 12 }}>Notifications</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Notification Email" hint="Pre-filled with group operator or admin email; edit to override">
            <Input type="email" placeholder="email@example.com" value={form.notifyEmail} onChange={set("notifyEmail")} />
          </Field>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 13px", borderRadius: 8, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div>
              <div style={{ color: "#1a1a1a", fontSize: 13 }}>Email on upload completion</div>
              <div style={{ color: "#6b7280", fontSize: 11 }}>Includes S3 destination path and file list</div>
            </div>
            <Toggle checked={form.notifyOnComplete} onChange={v => setForm(f => ({ ...f, notifyOnComplete: v }))}
              label="Email on upload completion" />
          </div>

          {form.notifyOnComplete && (
            <MessagePreview
              title="What will be sent when the upload finishes"
              deps={[project.id, form.notifyEmail, files.length]}
              fetcher={() => api.get(
                `/api/projects/${project.id}/submission-preview` +
                `?recipient=${encodeURIComponent(form.notifyEmail || "")}&file_count=${files.length}`)}
              emptyHint="Submission notifications are turned off in admin settings, so nothing would be sent."
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Slicing, and the checksum that comes with it ─────────────────────────────

// S3 allows at most 10 000 parts and refuses any part but the last below 5 MiB.
// Same formula as the backend's, with the same 9500 for headroom, so the two
// cannot disagree about how many parts a file has.
export const MIN_PART_BYTES = 8 * 1024 * 1024;
export const MAX_PARTS      = 9500;

export function choosePartSize(fileSize) {
  return Math.max(MIN_PART_BYTES, Math.ceil(fileSize / MAX_PARTS));
}

export function partCountFor(fileSize) {
  return Math.max(1, Math.ceil(fileSize / choosePartSize(fileSize)));
}

// Read in 4 MiB bites rather than materialising a whole ~95 MiB part. The Blob
// itself is what gets uploaded, so the part never has to exist in memory.
const HASH_CHUNK = 4 * 1024 * 1024;

/**
 * MD5 of one slice, while also feeding the running digest of the whole file.
 *
 * The whole-file digest is what goes into `md5.sum`, and it matters that it is
 * computed **here, from the local file** rather than by the server from the
 * bytes that arrived. A server-side digest hashes whatever turned up, so it
 * agrees with a corrupted transfer by construction — that is exactly how
 * `md5.sum` came to certify four empty objects in the dropped-folder incident.
 * This is a different pass than the write, so it can actually disagree.
 *
 * Callers must pass slices in order: `whole` is stateful.
 */
export async function hashSlice(blob, whole) {
  const part = new SparkMD5.ArrayBuffer();
  for (let off = 0; off < blob.size; off += HASH_CHUNK) {
    const buf = await blob.slice(off, off + HASH_CHUNK).arrayBuffer();
    part.append(buf);
    if (whole) whole.append(buf);
  }
  return part.end();
}

/**
 * Which parts still have to be sent, given what the server already holds.
 *
 * Kept separate and pure because the interesting case is not the arithmetic but
 * the decision: a part the server has is skipped for *upload* and still read for
 * *hashing*, or the whole-file digest would be computed over a hole.
 */
/**
 * Which parts still have to go over the wire.
 *
 * A part the server already holds is skipped — **except the last one, which is
 * always sent.** That is not redundancy: storing the final part is what makes
 * the server assemble the object, so a transfer whose assembly failed once (or
 * whose last part was stored but whose response was lost) has every byte in
 * storage and no way to finish. Skipping it left the parts to be thrown away
 * by the reaper days later.
 *
 * The cost is re-sending one part — at most `choosePartSize` bytes — and only
 * when resuming. The alternative was losing the whole transfer.
 */
export function partsToSend(partCount, held = []) {
  const have = new Set(held.map(Number));
  return Array.from({ length: partCount }, (_, i) => i + 1)
    .filter(n => !have.has(n) || n === partCount);
}

/**
 * Retry one slice a few times before giving up on the submission.
 *
 * Only worth doing because the server keeps the parts: a retry continues a
 * transfer rather than restarting one. Deliberately narrow — a 503 (storage
 * hiccup, assembly timeout) is worth another go, while a 4xx says the request
 * itself is wrong and repeating it just wastes the user's evening. An expired
 * session is never retried: the token is gone, and the point is to tell them so
 * while the parts are still held.
 *
 * **507 is the exception among 5xx.** "Insufficient Storage" is the one server
 * error that repeating cannot fix: the destination bucket is full, and the
 * backend only answers it for failures it has already classified as terminal.
 * Retrying it is not merely useless, it is what hid the cause — on 2026-09-05 a
 * 246 GB upload was retried into a full bucket four times across three days.
 */
const NO_RETRY_STATUSES = new Set([507]);

export async function withRetries(attempt, { tries = 3, waitMs = 2000, sleep } = {}) {
  const pause = sleep || (ms => new Promise(r => setTimeout(r, ms)));
  let lastError;
  for (let i = 0; i < tries; i++) {
    try {
      return await attempt();
    } catch (e) {
      if (e?.authExpired || /cancel/i.test(e?.message || "")) throw e;
      if (NO_RETRY_STATUSES.has(e?.status)) throw e;
      if (e?.status && e.status < 500 && e.status !== 429) throw e;
      lastError = e;
      if (i < tries - 1) await pause(waitMs * (i + 1));
    }
  }
  throw lastError;
}

/**
 * What to tell the user when the *final* call of a submission fails.
 *
 * A pure function so it can be tested without driving the wizard, the same
 * reason preflightEstimate and liveProjection are exported.
 *
 * The case that matters is an expired session: by the time `submit/finish`
 * runs, every byte is already in storage with an ETag from S3. Nothing failed
 * and nothing was lost — so this must not read as an error, and must not be
 * mistaken for a cancellation. In JOB-C58EB8 the user was shown nothing at all
 * after 21 h 24 m of successful transfer, and the submission had to be
 * recovered by hand from a backend log line.
 */
export function finishOutcomeNotice(error, jobId) {
  if (!error?.authExpired) return null;
  const which = jobId ? `“${jobId}”` : "the upload";
  return `Every file was transferred to storage successfully — nothing was lost. Your sign-in ` +
         `session ended before the submission could be recorded. Sign in again and open Jobs, ` +
         `where ${which} is waiting with a “Record upload” button.`;
}

export default function SubmissionWizard({ project, onSuccess, onCancel, sessionExpiresAt = null }) {
  const [step, setStep]       = useState(0);
  const [files, setFiles]     = useState([]);
  const [form, setForm]       = useState(() => makeInitialForm(project));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [progress, setProgress] = useState(null); // {name, totalLoaded, totalBytes}

  // What the screen reader is told, as opposed to what the screen shows. One
  // decile — 0..9 — so the live region below changes about ten times in an
  // upload rather than on every progress event. See the comment at its render.
  const announcedDecile = (progress && progress.totalBytes)
    ? Math.min(9, Math.floor((progress.totalLoaded / progress.totalBytes) * 10))
    : null;
  const [startedAt, setStartedAt] = useState(null); // for the live projection
  const xhrRef   = useRef(null);   // upload in flight, so it can be aborted
  const jobIdRef = useRef(null);   // server-side job row backing this submission
  // The minted JOB-XXXXXX, kept in state rather than only in jobIdRef (which
  // the finally clears) so a message can still name the job to look for.
  const [jobLabel, setJobLabel] = useState(null);

  // Which quotation this submission is for. The hook decides whether a picker
  // is needed at all; see useTransferOrder.
  const orderChoice = useTransferOrder(project.id);

  // An unfinished upload the server is still holding parts for.
  //
  // A browser cannot re-read a File after a reload — the handle is gone — so
  // resuming always needs the user to point at the same file again. The server
  // supplies everything else, and the match is on name and size because that is
  // all either side can compare without reading the file twice.
  const [resumable, setResumable] = useState([]);
  useEffect(() => {
    api.get(`/api/projects/${project.id}/submit/resumable`)
      .then(rs => setResumable(rs || []))
      .catch(() => {});   // a quotation that cannot be fetched is simply not made
  }, [project.id]);

  const resume = files.length === 1
    ? resumable.find(r => r.filename === files[0]?.file?.name) || null
    : null;

  // **A resume is not asked which quotation.** `handleSubmit` reuses the job the
  // server is already holding parts for and never calls submit/start, which is
  // the only request that carries `?order_id=` — so the answer would be
  // collected and discarded. The quotation was decided when that job started,
  // and the resumed transfer is filed under it whatever this page thinks.
  //
  // It read `orderChoice.blockedBy` alone until 2026-09-16, which left Submit
  // dead on a project with several live quotations until somebody answered a
  // question with no effect (found in the Stage 5 verification).
  const choiceNeeded = !resume && !!orderChoice.blockedBy;

  // An upload lives in this page: a refresh, a closed tab or a navigation kills
  // the XHR and the transfer is abandoned with nothing kept. Four uploads and
  // ~1.9 GB were lost to the refresh button in seven minutes on 2026-08-12 with
  // no warning at all, and the same reflex during a real multi-hour submission
  // costs the whole thing.
  //
  // Registered only while bytes are actually moving, and removed the moment
  // they stop — a page that always blocks navigation is a page people learn to
  // click through. The browser writes its own wording; returnValue is set only
  // because older browsers require it.
  useEffect(() => {
    if (!submitting) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [submitting]);

  const totalBytes = files.reduce((a, f) => a + f.file.size, 0);
  const preflight  = preflightEstimate(totalBytes, sessionExpiresAt);
  const projection = liveProjection(
    progress?.totalLoaded, progress?.totalBytes, startedAt, sessionExpiresAt);

  const canAdvance = () => {
    if (step === 0) return files.length > 0;
    return true;
  };

  const handleCancel = async () => {
    if (!jobIdRef.current || cancelling) return;
    setCancelling(true);
    try {
      // Ask the server first: it is what removes the partly-uploaded data,
      // and aborting the request beforehand would leave nothing running to
      // do that. Aborting afterwards just stops sending bytes we know are
      // about to be discarded.
      await api.post(`/api/jobs/${jobIdRef.current}/cancel`);
    } catch (e) {
      setError(e.message);
    } finally {
      if (xhrRef.current) xhrRef.current.abort();
      setCancelling(false);
    }
  };

  /**
   * The start call, and the two refusals the picker cannot prevent.
   *
   * The project can change between this page loading and somebody pressing
   * Submit, and `_order_for_transfer` says so with whichever status fits what
   * was sent: **409 naming every open quotation** when no id was named and
   * there is now more than one to choose from, **404** — "not one of this
   * project's, or it is no longer open" — when an id WAS named and that
   * quotation has since closed. Either sentence is the useful one and reaches
   * the reader unchanged through the error box below; re-reading the list here
   * is what makes the picker that appears next describe the project as it is
   * now rather than as it was.
   *
   * Only around the START call. Nothing re-reads anything once bytes are moving.
   */
  const startSubmission = async (url, body) => {
    try {
      return await api.post(url, body);
    } catch (e) {
      // **404 as well as 409, and the 404 is the one the picker causes.**
      // 409 is the server asking which quotation, which happens when none was
      // named. Once one HAS been named and it closed in the meantime — the
      // sweep ran, somebody cancelled it — `_order_for_transfer` answers 404
      // ("not one of this project's, or it is no longer open"), and recovering
      // only from 409 left the picker holding a dead choice with no way to
      // refresh it. Reloading on either is safe: it re-reads the list, and a
      // 404 that was really about the project shows the same refusal again.
      if (e?.status === 409 || e?.status === 404) await orderChoice.reload();
      throw e;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    setStartedAt(Date.now());   // baseline for the live throughput projection
    // Held for the whole submission. The token still goes the moment a 401
    // comes back, but being navigated to /login mid-message is exactly how the
    // original failure became invisible — and a full document navigation would
    // abort the upload XHR too.
    const releaseSignOut = deferSignOut();
    let sawExpiry = false;
    try {
      // Caught before the first byte moves. The backend refuses these too, but
      // an upload that runs for hours and only then hits S3's per-object limit
      // is the failure worth avoiding.
      const tooBig = files.find(f => f.file.size > MAX_FILE_BYTES);
      if (tooBig) {
        throw new Error(
          `${tooBig.file.name} is ${fmtBytes(tooBig.file.size)}, over the ` +
          `${fmtBytes(MAX_FILE_BYTES)} limit for a single object in S3. Split it before uploading.`,
        );
      }
      // Validate the project's configured S3 destination up front, before
      // spending potentially hours streaming data that would otherwise fail
      // at the very end. job_id + path (a server-minted date-time, not
      // user-editable) together tie this submission's files together and
      // form its S3 sub-folder.
      // Continuing an unfinished transfer reuses its job rather than minting a
      // new one — a second job would write to a second prefix, so the parts the
      // server is holding would belong to nothing and be reaped.
      // `orderChoice.query` is the `?order_id=` the routes take, empty unless the
      // person actually picked one. Sent HERE and nowhere else: this is the
      // gate, and it runs before the first byte moves. Nothing re-checks the
      // quotation mid-transfer — a check applied to a running upload tears down
      // work already done, which cost three incidents (2026-08-12).
      const { job_id: jobId, path, job_row_id: jobRowId } = resume
        ? { job_id: resume.job_id, path: resume.path, job_row_id: resume.job_row_id }
        : await startSubmission(
            `/api/projects/${project.id}/submit/start${orderChoice.query}`,
            // Sent so the job row can show progress against a real total, and so
            // the Jobs page names the files while they are still uploading.
            {
              files: files.map(f => ({ name: f.file.name, size: f.file.size })),
              // Snapshotted onto the job row, so an upload recorded after the fact
              // still honours what the submitter asked for here.
              notifyEmail: form.notifyEmail,
              notifyOnComplete: form.notifyOnComplete,
            },
          );
      jobIdRef.current = jobRowId ?? null;
      setJobLabel(jobId);

      // Stream files to S3 one at a time (via the backend relay — see
      // /submit/stream) so backend memory use stays bounded regardless of
      // file count or size. Each file's bytes go straight from the browser
      // to S3 without ever being fully buffered on either end.
      // totalBytes is computed once at the top of the component — it also
      // feeds the pre-flight estimate, so there is only one definition of
      // "how big is this submission".
      let doneBytes = 0;
      const uploaded = [];
      for (const f of files) {
        setProgress({ name: f.file.name, totalLoaded: doneBytes, totalBytes });

        const partSize  = choosePartSize(f.file.size);
        const partCount = partCountFor(f.file.size);
        // Parts the server already holds from an earlier attempt at this job.
        const held = (resume?.job_id === jobId && resume?.filename === f.file.name)
          ? resume.parts_held : [];

        // One pass over the file: each slice is hashed (for its own integrity
        // check) and folded into the whole-file digest that becomes md5.sum. A
        // slice the server already has is still *read*, because skipping it
        // would compute the whole-file digest over a hole.
        const whole = new SparkMD5.ArrayBuffer();
        // partsToSend is the one place that decides this, so the wizard and
        // DeliveryUploadPanel cannot drift. It used to be a local `skip` set
        // here, which is how the last part came to be skipped on a resume.
        const send  = new Set(partsToSend(partCount, held));
        const fileBaseline = doneBytes;
        let sentForFile = 0;
        let last = null;

        for (let n = 1; n <= partCount; n++) {
          const blob    = f.file.slice((n - 1) * partSize, Math.min(n * partSize, f.file.size));
          const partMd5 = await hashSlice(blob, whole);
          if (!send.has(n)) {
            sentForFile += blob.size;
            setProgress({ name: f.file.name, totalLoaded: fileBaseline + sentForFile, totalBytes });
            continue;
          }

          const headers = {
            "Content-Type": "application/octet-stream",
            "X-Filename": encodeURIComponent(f.file.name),
            "X-File-Size": String(f.file.size),
            "X-Job-Id": jobId,
            "X-Path": path,
            "X-Part-Number": String(n),
            "X-Part-Count": String(partCount),
            "X-Part-MD5": partMd5,
          };
          // Only the last part completes the object, so only it needs the
          // whole-file checksum — and only by then is it finished.
          if (n === partCount) headers["X-File-MD5"] = whole.end();

          const base = sentForFile;
          last = await withRetries(() => api.uploadPart(
            `/api/projects/${project.id}/submit/stream`,
            blob,
            headers,
            loaded => setProgress({
              name: f.file.name, totalLoaded: fileBaseline + base + loaded, totalBytes,
            }),
            xhr => { xhrRef.current = xhr; },
          ));
          sentForFile += blob.size;
        }

        // The last part's response carries the assembled object. A file whose
        // parts the server already had in full completes on its own record.
        uploaded.push(last && last.complete
          ? { key: last.key, name: last.name, size: last.size, md5: last.md5 }
          : { name: f.file.name, size: f.file.size });
        doneBytes += f.file.size;
      }
      xhrRef.current = null;

      const res = await api.post(`/api/projects/${project.id}/submit/finish`, {
        job_id: jobId, path, files: uploaded, notifyEmail: form.notifyEmail, notifyOnComplete: form.notifyOnComplete,
      });
      onSuccess(res?.job_id ?? jobId);
    } catch (e) {
      // Checked before the cancel test: an expired session is not something the
      // user asked for, and "Nothing was kept in storage" would be the exact
      // opposite of the truth about an upload that fully succeeded.
      const expired = finishOutcomeNotice(e, jobLabel);
      if (expired) {
        sawExpiry = true;
        setNotice(expired);
      } else if (/cancel/i.test(e.message)) {
        // A cancel is something the user just asked for, not a failure — say so
        // plainly instead of showing it as an error.
        setNotice("Upload cancelled. Nothing was kept in storage.");
      } else {
        setError(e.message);
      }
    } finally {
      xhrRef.current = null;
      jobIdRef.current = null;
      setSubmitting(false);
      setProgress(null);
      setStartedAt(null);
      // `handled` keeps the user on this page to read the notice above. Any
      // other outcome releases normally, so a session that really did end still
      // takes them to the sign-in screen.
      releaseSignOut({ handled: sawExpiry });
    }
  };

  if (!project.s3_configured) {
    return (
      <div>
        <div role="alert" style={{ padding: "16px 18px", borderRadius: 10, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#991b1b", fontSize: 14, marginBottom: 20 }}>
          This project has no storage yet — an admin sets the portal's storage up, and this project takes it then.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Step indicator */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : undefined }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                border: `2px solid ${i <= step ? G : "#d1d5db"}`,
                background: i < step ? G : i === step ? G_BG : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700,
                color: i < step ? "#fff" : i === step ? G_DARK : "#6b7280",
                transition: "all 0.3s",
              }}>
                {i < step ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 11, color: i === step ? G_DARK : "#6b7280", fontWeight: i === step ? 600 : 400, whiteSpace: "nowrap" }}>{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: "0 6px 16px", background: i < step ? G : "#e5e7eb", transition: "background 0.3s" }} />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div style={{ marginBottom: 20 }}>
        {step === 0 && <StepFiles files={files} setFiles={setFiles} />}
        {step === 1 && <StepReview project={project} form={form} setForm={setForm} files={files}
                                   orderChoice={orderChoice} resuming={!!resume} />}
      </div>

      {/* Before anything moves. Only when it does *not* fit — a warning shown
          on every upload is ignored within a week, and most uploads are small.
          The arithmetic is spelled out rather than reduced to a verdict,
          because the rate is an assumption the reader may know to be wrong. */}
      {!progress && preflight && !preflight.fits && (
        <div role="status" style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.35)", color: "#92400e", fontSize: 13 }}>
          <strong>This may not finish before your session ends.</strong>
          <div style={{ marginTop: 4 }}>
            {fmtBytes(totalBytes)} needs about <strong>{fmtHours(preflight.needed)}</strong> on a
            100 Mbit/s connection, and your session has <strong>{fmtHours(preflight.left)}</strong> left.
            If it ends first the upload is abandoned and nothing partial is kept.
          </div>
          <div style={{ marginTop: 4 }}>
            On a faster connection this will be quicker — the estimate assumes 45 GB per hour. If
            you are not sure, ask the facility about a direct transfer instead.
          </div>
        </div>
      )}

      {/* While running, from what the transfer is actually doing. This is the
          one that would have caught JOB-529B91 after minutes rather than
          after eight hours. */}
      {projection && !projection.fits && (
        <div role="status" style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 8, background: "rgba(185,28,28,0.06)", border: "1px solid rgba(185,28,28,0.35)", color: "#b91c1c", fontSize: 13 }}>
          <strong>At the current speed this upload will not finish in time.</strong>
          <div style={{ marginTop: 4 }}>
            {fmtBytes(projection.bytesPerHour)}/h measured so far — about{" "}
            <strong>{fmtHours(projection.remainingHours)}</strong> still to go, against{" "}
            <strong>{fmtHours(projection.left)}</strong> left in your session.
            Cancelling now costs you nothing that finishing would have saved.
          </div>
        </div>
      )}

      {progress && (
        // **`aria-live` moved OFF this container on 2026-09-08, and that is the
        // fix rather than a regression.** The old comment said polite meant the
        // update would be "available on demand". It does not: polite queues an
        // announcement for the next pause in speech, and this container's text
        // changes on every progress event — many times a second, for hours.
        // A screen reader either reads a stream of byte counts over everything
        // else the person does, or builds a backlog it is still working through
        // long after the upload ends. On a 246 GB transfer that is the whole
        // session.
        //
        // What replaces it is below: one polite region carrying a COARSE
        // message that changes about ten times in a whole upload. The numbers
        // stay on screen and stay in the progressbar, where a reader can go and
        // ask for them.
        <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#4b5563", marginBottom: 6, gap: 12 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "50%" }}>{progress.name}</span>
            <span>{fmtBytes(progress.totalLoaded)} / {fmtBytes(progress.totalBytes)} ({progress.totalBytes ? Math.round(progress.totalLoaded / progress.totalBytes * 100) : 0}%)</span>
          </div>
          <ProgressBar
            value={progress.totalBytes ? (progress.totalLoaded / progress.totalBytes) * 100 : 0}
            label={`Uploading ${progress.name}`}
          />
          {/* The announcement, at a tenth of the resolution of the display.
              `key` on the rounded decile means React replaces the node only
              when the decile changes, so the live region genuinely fires about
              ten times per upload rather than on every event. */}
          <SrOnly>
            <span aria-live="polite" key={announcedDecile}>
              {announcedDecile !== null && `Upload ${announcedDecile}0% complete`}
            </span>
          </SrOnly>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <Btn variant="secondary" onClick={handleCancel} disabled={cancelling || !jobIdRef.current}>
              {cancelling ? <><Spinner /> Cancelling…</> : "Cancel upload"}
            </Btn>
          </div>
        </div>
      )}

      {/* Shown only when there is actually something to continue. A standing
          banner about resumability would be ignored within a week; this one
          appears at the moment it changes what the button will do. */}
      {resume && !submitting && (
        <div role="status" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(0,150,64,0.06)", border: "1px solid rgba(0,150,64,0.30)", color: "#0b6b33", fontSize: 13 }}>
          <strong>This upload will continue where it stopped.</strong>{" "}
          {fmtBytes(resume.bytes_held)} of <strong>{resume.filename}</strong> is already in storage
          under {resume.job_id}; only the rest will be sent. The file is still read from start to
          finish to check it, which takes a moment before transfer begins.
        </div>
      )}

      {notice && (
        <div role="status" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "#f3f4f6", border: "1px solid #d1d5db", color: "#374151", fontSize: 13 }}>
          {notice}
        </div>
      )}

      {error && (
        <div role="alert" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* A dead button with no explanation is the exact failure
          DeliveryUploadPanel.test.jsx was written about — say why. */}
      {step === STEPS.length - 1 && choiceNeeded && !submitting && (
        <div role="status" style={{ marginBottom: 14, fontSize: 12, color: "#6b7280" }}>
          {orderChoice.blockedBy}
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Btn variant="secondary" onClick={step === 0 ? onCancel : () => setStep(s => s - 1)}>
          {step === 0 ? "Cancel" : "← Back"}
        </Btn>
        {step < STEPS.length - 1 ? (
          <Btn disabled={!canAdvance()} onClick={() => setStep(s => s + 1)}>Continue →</Btn>
        ) : (
          <Btn disabled={submitting || choiceNeeded} onClick={handleSubmit}>
            {submitting ? <><Spinner /> Uploading to S3…</> : "Submit →"}
          </Btn>
        )}
      </div>
    </div>
  );
}
